import { createTheme, responsiveFontSizes } from "@mui/material";
import { deepmerge } from "@mui/utils";
import { useContext } from "react";
import { usePreloadedQuery } from "react-relay";
import { PreloadedQueriesContext } from "../../../Relay/PreloadedQueriesProvider";
import { ThemeQuery } from "./ThemeQuery";
import { configureClientTheme, useMealTrackThemeConfigurations } from "./mealTrackThemeConfigurations";
/**
 * Builds the MUI theme for the current tenant by merging the MT base theme with the
 * client-specific theme loaded via GraphQL (ThemeQuery). Also derives the set of Google
 * Fonts to load from the typography values already present in that theme data.
 *
 * NOTE: This hook was previously named "useBrandingFetch" because it also issued a REST
 * call to /React/GetBrand and a Google Fonts API call. Both have been removed — all font
 * and theme data now comes from the preloaded ThemeQuery. The name is intentionally left
 * unchanged to minimize churn while branding/theming is still in active development.
 * Consider renaming to useClientTheme (or similar) once this area stabilises.
 */
export const useBrandingFetch = () => {
    var _a, _b;
    const { themeQueryReference } = useContext(PreloadedQueriesContext);
    const { MT_THEME, DEFAULT_FONTS } = useMealTrackThemeConfigurations();
    const themeData = themeQueryReference
        ? usePreloadedQuery(ThemeQuery, themeQueryReference)
        : undefined;
    const clientThemeConfigurations = configureClientTheme((_a = themeData === null || themeData === void 0 ? void 0 : themeData.theme) === null || _a === void 0 ? void 0 : _a.theme);
    // Merge default theme configurations with client-specific configurations.
    // - MT_THEME is the default Meal Track theme.
    // - clientThemeConfigurations contains theme configurations specific to the client.
    // - Using deepmerge combines the two objects, with clientThemeConfigurations taking precedence.
    const hasOverrides = clientThemeConfigurations && Object.keys(clientThemeConfigurations).length > 0;
    // Handle theme merge nuances.
    const theme = createTheme(hasOverrides ? deepmerge(MT_THEME, clientThemeConfigurations) : MT_THEME);
    const responsiveFontTheme = responsiveFontSizes(theme);
    // Derive the font list synchronously from the already-loaded theme data.
    // Any font families set by the tenant in their theme (via typography.*.fontFamily paths)
    // are picked up here and merged with the default set so GoogleFontLoader can inject them.
    const fonts = extractFontsFromTheme((_b = themeData === null || themeData === void 0 ? void 0 : themeData.theme) === null || _b === void 0 ? void 0 : _b.theme, DEFAULT_FONTS);
    return {
        fonts,
        theme: responsiveFontTheme,
    };
};
/**
 * Extracts unique Google Font families from a raw MUI theme JSON object.
 *
 * Walks all `fontFamily` values under `typography.*`, parses the first family name from
 * each CSS font-family string (e.g. `'"Roboto", "Helvetica", sans-serif'` → `'Roboto'`),
 * and merges any new families with the provided default font list.
 *
 * Fonts already present in `defaultFonts` retain their pre-defined weight variants.
 * New fonts discovered in the theme are loaded with the full standard weight set.
 */
const STANDARD_WEIGHTS = [
    "100", "100italic",
    "300", "300italic",
    "400", "400italic",
    "500", "500italic",
    "700", "700italic",
    "900", "900italic",
];
const extractFontsFromTheme = (themeJson, defaultFonts) => {
    const fontMap = new Map(defaultFonts.map((f) => [f.font, f]));
    const typography = themeJson === null || themeJson === void 0 ? void 0 : themeJson.typography;
    if (typography && typeof typography === "object") {
        Object.values(typography).forEach((variant) => {
            if (typeof (variant === null || variant === void 0 ? void 0 : variant.fontFamily) === "string") {
                // CSS font-family format: '"Roboto", "Helvetica", sans-serif' → 'Roboto'
                const firstFamily = variant.fontFamily.split(",")[0].trim().replace(/['"]/g, "");
                if (firstFamily && !fontMap.has(firstFamily)) {
                    fontMap.set(firstFamily, { font: firstFamily, weights: STANDARD_WEIGHTS });
                }
            }
        });
    }
    return Array.from(fontMap.values());
};
